/**
 * Coverage Optimizer - Main Application
 * Warehouse Coverage Optimizer
 */
import { useState, useEffect, useMemo } from 'react';
import { Warehouse, AlertCircle, Loader, Check } from 'lucide-react';
import OptimizerForm from './components/OptimizerForm';
import ResultsSummary from './components/ResultsSummary';
import InitialStateView from './components/InitialStateView';
import WebGLCoverageMap from './components/WebGLCoverageMap';
import { loadZip3Reference, loadZoneMatrix, loadZoneMatrices } from './lib/dataLoader';
import { optimize } from './lib/optimizer';
import { calculateCoverage, recalculateWarehouseOwnership } from './lib/coverage';
import type { 
  OptimizationParams, 
  OptimizationResult, 
  Zip3ReferenceMap, 
  ZoneMatrix,
  DestinationCoverage,
  WarehouseLocation,
} from './types';
import './App.css';

function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  const [result, setResult] = useState<OptimizationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deliveryGoal, setDeliveryGoal] = useState<2 | 3>(2);

  // Reference data
  const [zip3Reference, setZip3Reference] = useState<Zip3ReferenceMap | null>(null);
  const [zoneMatrix, setZoneMatrix] = useState<ZoneMatrix | null>(null);
  const [activeZoneMatrix, setActiveZoneMatrix] = useState<ZoneMatrix | null>(null); // Matrix used for current optimization

  // Load reference data on mount
  useEffect(() => {
    async function loadData() {
      try {
        setIsDataLoading(true);
        setDataError(null);

        console.log('Loading reference data...');
        const [zip3Ref, matrix] = await Promise.all([
          loadZip3Reference(),
          loadZoneMatrix(),
        ]);

        setZip3Reference(zip3Ref);
        setZoneMatrix(matrix);
        console.log('Reference data loaded successfully');
      } catch (err) {
        console.error('Failed to load reference data:', err);
        setDataError(
          err instanceof Error 
            ? err.message 
            : 'Failed to load reference data. Please check that the data files exist.'
        );
      } finally {
        setIsDataLoading(false);
      }
    }

    loadData();
  }, []);

  const handleOptimize = async (params: OptimizationParams) => {
    if (!zip3Reference) {
      setError('Reference data not loaded. Please refresh the page.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Small delay to allow UI to update
      await new Promise(resolve => setTimeout(resolve, 10));

      // Load appropriate zone matrix based on carrier selection
      let matrixToUse: ZoneMatrix;
      if (params.selectedCarriers && params.selectedCarriers.length > 0) {
        console.log(`Loading zone matrices for carriers: ${params.selectedCarriers.join(', ')}`);
        matrixToUse = await loadZoneMatrices(params.selectedCarriers);
      } else {
        console.log('Using consolidated zone matrix');
        matrixToUse = zoneMatrix || await loadZoneMatrix();
      }

      console.log('Running optimization with params:', params);
      const optimizationResult = optimize(params, matrixToUse, zip3Reference);
      
      console.log('Optimization complete:', optimizationResult);
      setResult(optimizationResult);
      setDeliveryGoal(params.deliveryGoal || 2);
      setActiveZoneMatrix(matrixToUse); // Store the matrix used for this optimization
    } catch (err) {
      console.error('Optimization failed:', err);
      setError(err instanceof Error ? err.message : 'Optimization failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWarehouseMove = (oldZip3: string, newZip3: string) => {
    if (!result || !zip3Reference || !activeZoneMatrix) return;
    
    // Validate the move
    if (oldZip3 === newZip3) return;
    
    // Check if destination already has a warehouse
    if (result.warehouses.some(w => w.zip3 === newZip3 && w.zip3 !== oldZip3)) {
      setError(`Warehouse already exists at ZIP3 ${newZip3}`);
      return;
    }
    
    // Check if destination exists in reference data
    if (!zip3Reference[newZip3]) {
      setError(`Invalid ZIP3: ${newZip3}`);
      return;
    }
    
    console.log(`Moving warehouse from ${oldZip3} to ${newZip3}`);
    
    // Update warehouse location
    const updatedWarehouses = result.warehouses.map(wh => 
      wh.zip3 === oldZip3
        ? {
            ...wh,
            zip3: newZip3,
            lat: zip3Reference[newZip3].lat,
            lng: zip3Reference[newZip3].lng,
            city: zip3Reference[newZip3].city,
            state: zip3Reference[newZip3].state,
          }
        : wh
    );
    
    // Recalculate global coverage
    const newCoverage = calculateCoverage(
      updatedWarehouses.map(w => w.zip3),
      activeZoneMatrix,
      zip3Reference,
      deliveryGoal
    );
    
    // Recalculate per-warehouse ownership
    const finalWarehouses = recalculateWarehouseOwnership(
      updatedWarehouses,
      activeZoneMatrix,
      zip3Reference,
      deliveryGoal
    );
    
    // Update result
    setResult({
      ...result,
      warehouses: finalWarehouses,
      coverage: newCoverage,
    });
    
    console.log('Warehouse moved successfully, coverage recalculated');
  };

  // Transform result data for map component
  const coverageData: DestinationCoverage[] = result?.coverage.coverageByDest ?? [];
  const warehouses: WarehouseLocation[] = result?.warehouses ?? [];
  
  // Calculate total US population from zip3Reference
  const totalUSPopulation = zip3Reference 
    ? Object.values(zip3Reference).reduce((sum, ref) => sum + (ref.population || 0), 0)
    : 0;
  
  // Calculate state populations for initial state tooltips
  const statePopulations = useMemo(() => {
    if (!zip3Reference) return new Map<string, number>();
    
    const map = new Map<string, number>();
    Object.values(zip3Reference).forEach(ref => {
      if (!ref.state) return;
      const current = map.get(ref.state) || 0;
      map.set(ref.state, current + (ref.population || 0));
    });
    return map;
  }, [zip3Reference]);

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-title">
            <Warehouse size={24} />
            <h1>Coverage Optimizer</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Data Loading State */}
        {isDataLoading && (
          <div className="loading-overlay">
            <div className="loading-card">
              <Loader size={32} className="spinner" />
              <div className="loading-text">Loading reference data...</div>
              <div className="loading-subtext">
                This may take a moment on first load
              </div>
            </div>
          </div>
        )}

        {/* Data Error State */}
        {dataError && (
          <div className="error-card">
            <AlertCircle size={24} />
            <div>
              <div className="error-title">Failed to Load Data</div>
              <div className="error-message">{dataError}</div>
              <div className="error-hint">
                Make sure you've run the data export scripts to generate the required JSON files.
              </div>
            </div>
          </div>
        )}

        {/* Main UI */}
        {!isDataLoading && !dataError && (
          <div className="content-grid">
            {/* Left Column - Form */}
            <div className="form-column">
              <div className="card">
                <div className="card-header">
                  <h2>Optimization Settings</h2>
                </div>
                <div className="card-body">
                  <OptimizerForm 
                    onOptimize={handleOptimize} 
                    isLoading={isLoading} 
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Results & Map */}
            <div className="results-column">
              {/* Error Display */}
              {error && (
                <div className="error-banner">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              {/* Results Summary */}
              {result && (
                <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="card-header">
                  {result.constraintsSummary ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#22c55e',
                      fontSize: '0.875rem',
                      fontWeight: 400,
                    }}>
                      <Check size={16} />
                      Found {result.constraintsSummary.requiredCount + result.constraintsSummary.newCount} warehouses: {result.constraintsSummary.requiredCount} required, {result.constraintsSummary.eligibleLocations.toLocaleString()} eligible locations, {result.constraintsSummary.newCount} new, {result.constraintsSummary.statesSearched.toLowerCase()} states searched.
                    </div>
                  ) : (
                    <h2>Coverage Results</h2>
                  )}
                </div>
                  <div className="card-body" style={{ padding: '0.75rem' }}>
                    <ResultsSummary 
                      coverage={result.coverage}
                      warehouses={result.warehouses}
                    />
                  </div>
                </div>
              )}

              {/* Map */}
              {result && coverageData.length > 0 && (
                <div className="card">
                  <div className="card-body">
                    <WebGLCoverageMap 
                      coverageData={coverageData}
                      warehouses={warehouses}
                      deliveryGoal={deliveryGoal}
                      onWarehouseMove={handleWarehouseMove}
                      zip3Reference={zip3Reference || undefined}
                    />
                  </div>
                </div>
              )}

              {/* Initial State - Before Optimization */}
              {!result && zip3Reference && (
                <>
                  <div className="card" style={{ marginBottom: '1rem' }}>
                    <div className="card-header">
                      <h2>Coverage Results</h2>
                    </div>
                    <div className="card-body">
                      <InitialStateView totalPopulation={totalUSPopulation} />
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-body">
                      <WebGLCoverageMap 
                        coverageData={[]}
                        warehouses={[]}
                        deliveryGoal={deliveryGoal}
                        initialStateMode={true}
                        statePopulations={statePopulations}
                        zip3Reference={zip3Reference || undefined}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          Warehouse Coverage Optimization Engine
        </div>
      </footer>
    </div>
  );
}

export default App;

